import {
    AddCircle16Regular,
    ClipboardBulletList20Filled,
    ClipboardBulletList20Regular,
    ClipboardTaskListLtr20Filled,
    TaskListLtrRegular,
} from '@fluentui/react-icons';
import { Button, makeStyles, Text, tokens } from '@fluentui/react-components';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useAccess } from '../../../../auth/useAccess';
import { BrandCard, Column, Row } from '../../../common';
import { AddEditTaskDialog } from '../../../dialogs/AddEditTaskDialog';
import { Task, TaskStatus, UserAccount } from '../../../../data/types';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { useState } from 'react';
import { CaseTaskTable } from './CaseTasksTable';
import { useLocation, useNavigate } from 'react-router';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { appTokens } from '../../../common/Styles/appTokens';
import { DropdownMenu } from '../../../controls/Menus/DropdownMenu';

enum MyTabs {
    mine = 'mine',
    all = 'all',
}

const useStyles = makeStyles({
    emptyPanel: {
        paddingBlock: '1rem',
    },
    tabList: {},
    tab: {
        width: '100%',
        height: '2.5rem',
        ':hover': {
            backgroundColor: `${tokens.colorNeutralBackground2Hover} !important`,
        },
    },
    viewTabList: {
        paddingBlockEnd: '.25rem',
        width: 'auto',
    },
});

export const CaseTasksPage = () => {
    const { caseForm, milestoneService, taskService, timeTrackingService, noteService } = useCaseServicesContext()!;
    const caseObj = caseForm.values;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = primaryAccount as UserAccount;
    const navTo = useNavigate();
    const { pathname, state } = useLocation();
    const selectedTab = state.subTab || TaskStatus.open;

    const access = useAccess(user);
    const canSeeAllTasks = access.cases.canEditCaseAsAdmin(caseObj.caseId);

    //Vars
    const openTasks = taskService.tasks;

    //State
    const [viewTab, setViewTab] = useState<string>(canSeeAllTasks ? MyTabs.all : MyTabs.mine);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Logic
    const filterTasks = (task: Task) => {
        if (task.status == selectedTab || selectedTab == 'All') {
            if (viewTab == MyTabs.mine) {
                return task.userId == user.userId;
            } else {
                return true;
            }
        } else {
            return false;
        }
    };
    const sortTasks = (a: Task, b: Task) => {
        const statusOrder = {
            [TaskStatus.new]: 0,
            [TaskStatus.open]: 1,
            [TaskStatus.completed]: 2,
            [TaskStatus.closed]: 3,
        };
        const statusComparison = (statusOrder[a?.status as TaskStatus] || 0) - (statusOrder[b?.status as TaskStatus] || 0);
        if (statusComparison !== 0) {
            return statusComparison;
        }
        const msA = milestoneService?.milestones.find((x) => x.milestoneId == a.milestoneId);
        const msB = milestoneService?.milestones.find((x) => x.milestoneId == b.milestoneId);
        return (msA?.sortKey || 0) - (msB?.sortKey || 0);
    };
    const switchTab = (newTab: TaskStatus) => {
        navTo(pathname, { state: { ...state, subTab: newTab } });
    };

    //UI Events
    const onViewTabSelect = (option: string) => {
        setViewTab(option);
    };
    const onStatusOptionChanged = (option: string) => {
        switchTab(option as TaskStatus);
    };

    return (
        <Column flex='1' padding='px-2 py-1'>
            <BrandCard
                flex='1'
                title='Tasks'
                headerIcon={
                    <TaskListLtrRegular />
                }
                headerComponent={
                    <Column flex='1' align='right'>
                        <Row width='auto' padding='pr-4'>
                            <Column width='auto' vAlign='middle'>
                                <DropdownMenu
                                    options={['All', TaskStatus.open, TaskStatus.completed, TaskStatus.closed].map(
                                        (option) => ({
                                            label: `${option} Tasks`,
                                            icon:
                                                option == 'All' ? (
                                                    <ClipboardBulletList20Regular />
                                                ) : option == TaskStatus.open ? (
                                                    <ClipboardBulletList20Filled color={appTokens.foregrounds.primary} />
                                                ) : (
                                                    <ClipboardTaskListLtr20Filled
                                                        color={option == TaskStatus.completed ? 'green' : 'red'}
                                                    />
                                                ),
                                            value: option,
                                        }),
                                    )}
                                    title='Filter Tasks'
                                    size='medium'
                                    initialValue={selectedTab || TaskStatus.open}
                                    onOptionChanged={onStatusOptionChanged}
                                    width='fit-content'
                                />
                            </Column>
                            {canSeeAllTasks && (
                                <Column width='auto' vAlign='middle'>
                                    <>
                                        <DropdownMenu
                                            options={[MyTabs.mine, MyTabs.all].map((option) => ({
                                                label: option == MyTabs.mine ? 'My Tasks' : 'All Users Tasks',
                                                value: option,
                                            }))}
                                            title='View'
                                            size='medium'
                                            initialValue={viewTab}
                                            onOptionChanged={onViewTabSelect}
                                            width='fit-content'
                                        />
                                    </>
                                </Column>
                            )}
                        </Row>
                    </Column>
                }
                action={
                    <Column width='auto' padding='pr-2'>
                        {access.cases.canCreateTasks() && (
                            <AddEditTaskDialog onSubmit={taskService.upsertTaskAsync} initialCaseId={caseObj.caseId}>
                                <Button
                                    icon={<AddCircle16Regular />}
                                    appearance='secondary'
                                    data-testid='case-tasks-add-task-button'
                                >
                                    Add Task
                                </Button>
                            </AddEditTaskDialog>
                        )}
                    </Column>
                }
            >
                <Column maxWidth='calc(100% - .75rem)' flex='1'>
                    {!!openTasks.length && (
                        <>
                            <CaseTaskTable
                                tasks={openTasks.filter(filterTasks).sort(sortTasks)}
                                bodyHeight={appTokens.pageHeight('17.5rem')}
                                onSubmit={taskService.upsertTaskAsync}
                                onComplete={taskService.onComplete}
                                onClose={taskService.onClose}
                                onReopen={taskService.onReopen}
                                showAssigned={viewTab == MyTabs.all}
                                showStatus={true}
                                timeTrackingService={timeTrackingService}
                                milestoneService={milestoneService}
                                noteService={noteService}
                                select={false}
                            />
                        </>
                    )}

                    {!openTasks.length && (
                        <Column className={styles.emptyPanel} gap='medium' align='center'>
                            <Text size={500}>You are up to date!</Text>
                            <Text size={400}>There are no Tasks remaining.</Text>
                        </Column>
                    )}
                </Column>
            </BrandCard>
        </Column>
    );
};
